import BackIcon from '@mui/icons-material/ArrowBackIosNew'
import ForwardIcon from '@mui/icons-material/ArrowForwardIos'
import { Dialog, DialogContent, DialogTitle, IconButton, MobileStepper, SxProps, Theme } from '@mui/material'
import { FC, ReactNode, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { useScreenSize } from '../../hooks/useScreenSize'
import CloseIcon from '../Icons/close'

interface BaseDialogProps {
    name: Dialogs
    title?: string
    subtitle?: string
    children?: ReactNode
    onClose?: () => void
    nonClickableBackdrop?: boolean
    onBack?: () => void
    showBackArrow?: boolean
    showClose?: boolean
    gap?: number | string
    horizontalCentered?: boolean
    verticalCentered?: boolean
    autoHeight?: boolean
    forceBlock?: boolean
    fullScreen?: boolean
    borderColor?: string
    paddingLess?: boolean
    padding?: string
    titleColor?: string
    subtitleColor?: string
    stepper?: {
        steps: number
        activeStep: number
        onBack?: () => void
        onNext?: () => void
    }
    sx?: SxProps<Theme>
}

const BaseDialog: FC<BaseDialogProps> = (props) => {
    const {
        name,
        title,
        subtitle,
        children,
        showBackArrow = false,
        onBack,
        showClose = true,
        onClose,
        nonClickableBackdrop,
        gap,
        horizontalCentered,
        verticalCentered = true,
        autoHeight,
        forceBlock = false,
        fullScreen,
        borderColor,
        paddingLess,
        padding,
        titleColor,
        subtitleColor,
        stepper,
        sx,
    } = props
    const { isMobile } = useScreenSize()
    const [active, setActive] = useState(true)
    const { unregisterDialog } = useDialogs()

    const onCloseHandler = () => {
        setActive(false)
        unregisterDialog(name, onClose)
    }

    const onBackHandler = () => {
        onBack && onBack()
    }

    return (
        <Dialog
            open={active}
            onClose={nonClickableBackdrop ? undefined : onCloseHandler}
            horizontalCentered={horizontalCentered}
            verticalCentered={verticalCentered}
            autoHeight={autoHeight}
            forceBlock={forceBlock} // This prop is used to force the dialog to block (related to rule root+root)
            fullScreen={fullScreen}
            borderColor={borderColor}
            paddingLess={paddingLess}
            padding={padding}
            sx={sx}
        >
            {title && <DialogTitle color={titleColor}>{title}</DialogTitle>}
            {subtitle && <DialogTitle color={subtitleColor}>{subtitle}</DialogTitle>}
            {showBackArrow && (
                <IconButton
                    sx={{
                        position: 'absolute',
                        left: { xs: '15px', md: '25px' },
                        top: { xs: '20px', md: '25px' },
                    }}
                    size={isMobile ? 'small' : 'large'}
                    onClick={onBackHandler}
                >
                    <BackIcon fontSize={isMobile ? 'small' : 'large'} />
                </IconButton>
            )}
            {showClose && (
                <IconButton
                    id="close-button"
                    sx={{
                        position: 'absolute',
                        right: { xs: paddingLess ? '0px' : '24px', md: '35px' },
                        top: { xs: paddingLess ? '0px' : '24px', md: '30px' },
                        zIndex: 1001,
                    }}
                    size={isMobile ? 'small' : 'large'}
                    onClick={onCloseHandler}
                >
                    <CloseIcon />
                </IconButton>
            )}

            {stepper && stepper.onBack && (
                <IconButton
                    sx={{
                        position: 'absolute',
                        zIndex: 100,
                        left: { xs: '0px', md: '8px' },
                        top: '45%',
                    }}
                    size={isMobile ? 'small' : 'large'}
                    disabled={stepper.activeStep <= 0}
                    onClick={stepper.onBack}
                >
                    <BackIcon fontSize={isMobile ? 'small' : 'large'} />
                </IconButton>
            )}
            {stepper && stepper.onNext && (
                <IconButton
                    sx={{
                        position: 'absolute',
                        zIndex: 100,
                        right: { xs: '0px', md: '8px' },
                        top: '45%',
                    }}
                    size={isMobile ? 'small' : 'large'}
                    disabled={stepper.activeStep >= stepper.steps - 1}
                    onClick={stepper.onNext}
                >
                    <ForwardIcon fontSize={isMobile ? 'small' : 'large'} />
                </IconButton>
            )}

            <DialogContent gap={gap} verticalCentered={verticalCentered}>
                {children}
            </DialogContent>

            {stepper && (
                <MobileStepper
                    position="bottom"
                    steps={stepper.steps}
                    activeStep={stepper.activeStep}
                    backButton={undefined}
                    nextButton={undefined}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: '16px',
                    }}
                />
            )}
        </Dialog>
    )
}

export default BaseDialog
